import { Fragment, useMemo } from 'react'
import styled from 'styled-components'
import {
  ProductsListItemResponseDto,
  RecentlyBoughtItemResponseDto,
  SearchProductResponseDtoV3,
} from '@/apis/rest/generated/types'
import { useBackAction } from '@/components/common/back-action/use-back-action'
import { JustViewedProductList } from '@/components/domains/list/just-viewed-product-list'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { SearchResultBrandProductsList } from '@/components/domains/search/result/SearchResultBrandProductsList'
import SearchResultEmpty from '@/components/domains/search/result/SearchResultEmpty'
import { SearchResultRecentlyBoughtProducts } from '@/components/domains/search/result/SearchResultRecentlyBoughtProducts'
import { ProductLocation } from '@/constants/product-location.const'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { SearchResultContainer } from '@/containers/search/search-result.context'
import { AdPetType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'

type HandleClickProductCardTracker = ({
  item,
  index,
  properties,
}: {
  item: ProductTypeDefinedByFrontend
  index: number
  properties?: Record<string, unknown>
}) => void

type SearchResultProductListProps = {
  searchKeyword: string
  searchLocation: string
  petType: AdPetType
  recentlyBoughtProducts: RecentlyBoughtItemResponseDto[]
  onClickProductCardTracker: HandleClickProductCardTracker
  searchBrandProducts?: SearchProductResponseDtoV3
}

const SkeletonProductLength = 6

export const SearchResultProductList = ({
  searchKeyword,
  searchLocation,
  petType,
  recentlyBoughtProducts,
  onClickProductCardTracker,
  searchBrandProducts,
}: SearchResultProductListProps) => {
  const { isLoading, ref, isSuccess, searchProducts, isFetchingNextPage, isEmpty } =
    SearchResultContainer.useContainer()

  const backAction = useBackAction('recommend-list-for-search-result')

  const handleClickProductCard = ({ item, index }: { item: ProductTypeDefinedByFrontend; index: number }) => {
    onClickProductCardTracker({ item, index })
  }

  const memoizedGroupByFlattedProductList = useMemo(() => {
    const paged = searchProducts ? searchProducts.pages.map((page) => page.products || []) : []
    const flatted = paged.flatMap((product) => product)
    return flatted.reduce<ProductsListItemResponseDto[][]>((acc, cur, idx, arr) => {
      if (idx % 2 === 0) return [...acc, ...[arr.slice(idx, idx + 2)]]
      return acc
    }, [])
  }, [searchProducts])

  return (
    <StyledSearchResultProductList className="search-result-product-list">
      {isEmpty && (
        <>
          <SearchResultEmpty />
          {recentlyBoughtProducts.length > 0 && (
            <>
              <hr className="search-result-product-list__empty-divider" />
              <div className="search-result-product-list__empty-wrapper-recently-bought-products">
                <SearchResultRecentlyBoughtProducts
                  searchLocation={searchLocation}
                  searchKeyword={searchKeyword}
                  recentlyBoughtProducts={recentlyBoughtProducts}
                  petType={petType}
                />
              </div>
            </>
          )}
        </>
      )}
      {isSuccess && memoizedGroupByFlattedProductList.length > 0 && (
        <div>
          {memoizedGroupByFlattedProductList.map((list, groupedIndex) => {
            const queue = backAction.front()
            const isShowJustViewedProduct = list.some((p) => backAction.contain(`${groupedIndex}::${p.id}`))
            const justViewedProduct = list.find((p) => queue?.condition?.includes(p.id.toString()))

            const isShowRecentlyBoughtProducts = groupedIndex === 0 && recentlyBoughtProducts.length > 0

            const isShowBrandProducts =
              groupedIndex === 0 && searchBrandProducts?.products && searchBrandProducts?.products.length > 0
            return (
              <Fragment key={groupedIndex}>
                <StyledSearchResultProductListContainer>
                  {list?.map((data, index) => {
                    if (!data) {
                      return null
                    }

                    const product = transformProductTypeDefinedByFrontend(data)

                    return (
                      <Fragment key={product.id}>
                        <div className="search-result-product-list__card" ref={ref}>
                          <NewProductCard
                            product={product}
                            size={PRODUCT_CARD_SIZES.half}
                            rankingNumber={index}
                            hasCartButton
                            onClickProductCard={() => {
                              handleClickProductCard({ item: product, index })
                              backAction.push(`${groupedIndex.toString()}::${product.id}`)
                            }}
                            trackerData={{
                              searchKeyword,
                              searchLocation,
                              addLocation: ProductLocation.SearchResult,
                              listIndex: index,
                            }}
                          />
                        </div>
                      </Fragment>
                    )
                  })}
                </StyledSearchResultProductListContainer>
                {isShowJustViewedProduct && justViewedProduct && (
                  <StyledJustViewedProductListWrapper>
                    <JustViewedProductList
                      name="recommend-list-for-search-result"
                      justViewedProduct={transformProductTypeDefinedByFrontend(justViewedProduct)}
                      onClickProductCardTracker={onClickProductCardTracker}
                      trackerData={{
                        location: 'search',
                        addLocation: 'search_result',
                      }}
                    />
                  </StyledJustViewedProductListWrapper>
                )}
                {isShowBrandProducts && (
                  <div className="search-result-product-list__brand-products">
                    <SearchResultBrandProductsList
                      products={searchBrandProducts?.products || []}
                      brands={searchBrandProducts?.brands || []}
                      searchKeyword={searchKeyword}
                      petType={petType}
                      searchLocation={searchLocation}
                    />
                  </div>
                )}
                {isShowBrandProducts && isShowRecentlyBoughtProducts && (
                  <hr className="search-result-product-list__empty-divider search-result-product-list__empty-divider--brand-products" />
                )}
                {isShowRecentlyBoughtProducts && (
                  <div className="search-result-product-list__recently-bought-products">
                    <SearchResultRecentlyBoughtProducts
                      searchLocation={searchLocation}
                      searchKeyword={searchKeyword}
                      recentlyBoughtProducts={recentlyBoughtProducts}
                      petType={petType}
                      onClickProductCardTracker={onClickProductCardTracker}
                    />
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      )}
      {isFetchingNextPage ||
        (isLoading && (
          <StyledSkeleton>
            <SkeletonProduct length={SkeletonProductLength} size={ProductThumbnailSizeEnum.Fill} />
          </StyledSkeleton>
        ))}
    </StyledSearchResultProductList>
  )
}

const StyledSearchResultProductList = styled.section`
  .search-result-product-list {
    &__empty-divider {
      margin: 0 -1.6rem 0;
      border: none;
      width: calc(100% + 3.2rem);
      height: 0.8rem;
      background-color: ${({
        theme: {
          color: { gray70 },
        },
      }) => gray70};
    }
    &__empty-divider--brand-products {
      margin: 0;
      width: 100%;
      margin-bottom: 1.6rem;
    }
    &__empty-wrapper-recently-bought-products {
      padding: 2.4rem 0 calc(env(safe-area-inset-bottom) + 2.4rem);
    }
    &__card {
      margin-bottom: 2.4rem;
    }
    &__recently-bought-products {
      margin-bottom: 2.4rem;

      & > div:first-child {
        & > *:first-child,
        & > *:nth-child(2) > div > div > *:first-child {
          padding-left: 1.6rem;
        }
        & > *:nth-child(2) > div > div > *:last-child {
          padding-right: 1.6rem;
        }
      }
    }
  }
`

const StyledSearchResultProductListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 8px;
  padding: 0 1.6rem;
`

const StyledSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 8px;

  & > div {
    &:nth-child(1),
    &:nth-child(2) {
      margin-top: 0;
    }
  }
`

const StyledJustViewedProductListWrapper = styled.div`
  padding: 2.4rem 0;
`
